import Container from "../element/container";
import { Fragment, useEffect, useRef, useState } from "react";

//lib
import { MDBModal, MDBModalDialog, MDBModalContent } from "mdb-react-ui-kit";
import { RemoveScroll } from "react-remove-scroll";
import { motion, AnimatePresence } from "framer-motion";

//redux
import { useDispatch, useSelector } from "react-redux";
import {
  setAnimationModal,
  setShowModal,
} from "../../redux/reducer/modal.reducer";
import ReactDOM from "react-dom";
import useWindowDimension from "../hook/use.window.dimension";
import { Icon } from "@iconify/react/dist/iconify.js";
import Button from "../element/button";

export default function FullModal({
  show,
  close,
  showCloseButton,
  content,
  footer,
  centered,
  screenSize,
  scrollable,
  className,
  dialogClassName,
  bodyClassName,
  contentClassName,
  modalRef,
  inline = false,
  inlineDesktop = false,
  independent = false,
  portalTargetId,
  lockScroll = true,
  closeOnBackdrop = true,
}) {
  const dispatch = useDispatch();
  const { width } = useWindowDimension();
  const { showModal, isAnimationModal } = useSelector((state) => state.modal);
  const isVisible = independent ? show : showModal;
  const ScrollGuard = lockScroll ? RemoveScroll : Fragment;
  const contentRef = useRef(null);
  // MDBModalContent is a plain function component, so a ref on it never
  // resolves to a DOM node. The motion shell is the closest element we own.
  const shellRef = useRef(null);

  const handleCloseModalWithDelay = () => {
    if (independent) {
      close?.();
      return;
    }

    dispatch(setShowModal(false));
    setTimeout(() => {
      dispatch(setAnimationModal(true));
      close?.();
    }, 500);
  };

  useEffect(() => {
    if (!independent) {
      dispatch(setShowModal(show));
    }
  }, [dispatch, independent, show]);

  const [frameEl, setFrameEl] = useState(null);

  useEffect(() => {
    const defaultTargetId = width >= 992 ? "site-frame" : "mobile-frame";
    const el =
      document.getElementById(portalTargetId || defaultTargetId) ||
      document.body;
    setFrameEl(el);
  }, [portalTargetId, width]);

  useEffect(() => {
    if (!isVisible) return undefined;

    const resetScroll = () => {
      const anchor = shellRef.current || contentRef.current;
      if (!anchor) return;

      const root = anchor.closest(".full-modal") || anchor;
      [root, ...root.querySelectorAll("*")].forEach((element) => {
        if (!element.scrollTop && !element.scrollLeft) return;
        const behavior = element.style.scrollBehavior;
        element.style.scrollBehavior = "auto";
        element.scrollTop = 0;
        element.scrollLeft = 0;
        element.style.scrollBehavior = behavior;
      });
    };

    // The content fades in behind a framer transition, so run once immediately
    // and again after it settles in case the body was still filling in.
    const frame = window.requestAnimationFrame(resetScroll);
    const timers = [80, 400].map((delay) => window.setTimeout(resetScroll, delay));

    return () => {
      window.cancelAnimationFrame(frame);
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [isVisible, frameEl]);

  if (inline || (inlineDesktop && width >= 992)) {
    return (
      <section className={`full-modal ${className} inline-modal`}>
        <div ref={contentRef} className={`modal-content ${contentClassName ?? ""}`}>
          {content}
          {footer}
        </div>
      </section>
    );
  }

  return (
    <>
      {/* Portal target */}
      {frameEl &&
        ReactDOM.createPortal(
          <AnimatePresence>
            {isVisible && (
              <ScrollGuard>
                <MDBModal
                  className={`full-modal ${className}`}
                  tabIndex="-1"
                  show={true}
                >
                  {close && closeOnBackdrop && (
                    <Container tag="div"
                      style={{
                        height: "100%",
                        width: "100%",
                        position: "absolute",
                      }}
                      onClick={() => handleCloseModalWithDelay()}
                    ></Container>
                  )}
                  <motion.div
                    ref={shellRef}
                    className="modal-motion-shell"
                    initial={
                      isAnimationModal && {
                        scale: 0.2,
                      }
                    }
                    animate={
                      isAnimationModal && {
                        scale: 1,
                        opacity: 1,
                        transition: {
                          duration: 0.5,
                        },
                      }
                    }
                    exit={
                      isAnimationModal && {
                        opacity: [1, 0],
                        scale: [1, 0.8],
                        transition: {
                          duration: 0.3,
                        },
                      }
                    }
                  >
                    <MDBModalDialog
                      centered={centered}
                      size={`${screenSize}`}
                      className={dialogClassName}
                      scrollable={scrollable ? true : false}
                    >
                      <MDBModalContent className={contentClassName}>
                        {showCloseButton && (
                          <Button
                            className="modal-close"
                            onClick={() => handleCloseModalWithDelay()}
                          >
                            <Icon icon="majesticons:close-line" />
                          </Button>
                        )}
                        <motion.div
                          className="modal-content-shell"
                          initial={
                            isAnimationModal && {
                              opacity: 0,
                            }
                          }
                          animate={
                            isAnimationModal && {
                              opacity: 1,
                              transition: {
                                delay: 0.3,
                                duration: 0.3,
                              },
                            }
                          }
                          exit={
                            isAnimationModal && {
                              opacity: 0,
                              transition: {
                                duration: 0.3,
                              },
                            }
                          }
                        >
                          {content}
                        </motion.div>
                      </MDBModalContent>
                    </MDBModalDialog>
                  </motion.div>
                  {footer}
                </MDBModal>
              </ScrollGuard>
            )}
          </AnimatePresence>,
          frameEl, // ⬅️ appended inside .mobile-frame
        )}
    </>
  );
}
